import React from 'react';
import { NewBadge } from '@fiverr-private/badges';
import { Container, Stack } from '@fiverr-private/layout_components';
import { atoms } from '@fiverr-private/theme';
import { Link, Typography } from '@fiverr-private/typography';
import type { ExtendedTransformedCmsConfigItem } from './transformer/types';
import { hasLink, type CategoriesMenuGroup } from './utils';
import { columnEnter, columnItem, columnsGrid, focusItem, subLink } from './CategoriesMenuPanel.ve.css';

const noListStyle = atoms({ listStyleType: 'none' });
const block = atoms({ display: 'block' });

const MAX_STAGGER_STEPS = 3;

interface SubcategoryGridProps {
    groups: CategoriesMenuGroup[];
    columnDelaysMs: [number, number];
    onLinkClick?: (item: ExtendedTransformedCmsConfigItem, heading?: ExtendedTransformedCmsConfigItem) => void;
}

export const SubcategoryGrid = ({ groups, columnDelaysMs, onLinkClick }: SubcategoryGridProps) => {
    const [startMs, secondMs] = columnDelaysMs;
    const delayMs = (index: number) => startMs + Math.min(index, MAX_STAGGER_STEPS) * (secondMs - startMs);

    return (
        <Container className={columnsGrid} paddingLeft="3" paddingRight="6">
            {groups.map(({ heading, links }, index) => {
                const headingId = heading ? `categories-group-${heading.id}` : undefined;

                return (
                    <Container
                        as="section"
                        key={heading?.id ?? 'links'}
                        className={`${columnEnter} ${columnItem}`}
                        role="group"
                        aria-labelledby={headingId}
                        style={{ animationDelay: `${delayMs(index)}ms` }}
                    >
                        {heading && (
                            <Typography
                                as="h3"
                                id={headingId}
                                size="b_sm"
                                fontWeight="semibold"
                                color="grey_1200"
                                marginBottom="1"
                            >
                                {hasLink(heading) ? (
                                    <Link
                                        className={`${block} ${focusItem}`}
                                        href={heading.url}
                                        onClick={() => onLinkClick?.(heading)}
                                        paddingX="3"
                                        paddingY="1"
                                        color="grey_1200"
                                        decoration={{ default: 'none', hover: 'underline' }}
                                    >
                                        {heading.displayName}
                                    </Link>
                                ) : (
                                    <Container className={block} paddingX="3" paddingY="1">
                                        {heading.displayName}
                                    </Container>
                                )}
                            </Typography>
                        )}
                        <Stack as="ul" className={noListStyle} direction="column" gap="0" margin="0" padding="0">
                            {links.map((link) => (
                                <li key={link.id}>
                                    <Link
                                        className={`${block} ${subLink}`}
                                        href={link.url}
                                        onClick={() => onLinkClick?.(link, heading)}
                                        size="b_sm"
                                        decoration="none"
                                        color={{ default: 'grey_1100', hover: 'grey_1200' }}
                                    >
                                        <Stack
                                            alignItems="center"
                                            gap="1.5"
                                            minHeight="40px"
                                            paddingX="3"
                                            paddingY="1"
                                            borderRadius="lg"
                                            backgroundColor={{
                                                default: 'transparent',
                                                hover: 'grey_200',
                                                active: 'grey_300',
                                            }}
                                        >
                                            {link.displayName}
                                            {link.badge === 'new' && <NewBadge />}
                                        </Stack>
                                    </Link>
                                </li>
                            ))}
                        </Stack>
                    </Container>
                );
            })}
        </Container>
    );
};
